import {useState} from "react";

import type {AiVerdict} from "@/api/extractionApprovals";
import {ClickDetailPopover} from "@/components/ClickDetailPopover";
import {AiVerdictPill} from "@/components/extraction/AiVerdictPill";

type WikidataAiVerdict = AiVerdict & {suggested_fixes?: Array<Record<string, unknown>>};

function verdictTitle(overall: string | undefined, judgeFailure = false): string {
  const o = String(overall ?? "").toLowerCase();
  if (judgeFailure) return "AI verification did not complete";
  if (o === "fail") return "Why AI flagged this as wrong";
  if (o === "abstain") return "Why AI is unsure";
  if (o === "partial") return "Why AI marked this as partly correct";
  if (o === "full" || o === "pass") return "Why AI thinks this looks right";
  return "AI verdict details";
}

function formatWhen(at: string | null | undefined): string | null {
  if (!at) return null;
  try {
    return new Date(at).toLocaleString();
  } catch {
    return at;
  }
}

export interface WikidataItemAiVerdictBadgeProps {
  verdict: AiVerdict | null | undefined;
  localId?: string;
  /** True while AI verify is running for this row and no verdict has landed yet. */
  judging?: boolean;
}

export function WikidataItemAiVerdictBadge({verdict, localId, judging = false}: WikidataItemAiVerdictBadgeProps) {
  const [popover, setPopover] = useState<{x: number; y: number} | null>(null);

  if (!verdict && judging) {
    return <AiVerdictPill verdict={null} judging />;
  }

  if (!verdict) {
    return <AiVerdictPill verdict={null} />;
  }

  const v = verdict as WikidataAiVerdict;
  const fixes = v.suggested_fixes ?? [];
  const when = formatWhen(v.judged_at);

  return (
    <>
      <button
        type="button"
        className="cursor-pointer text-left"
        title="Click for full AI explanation"
        data-testid={localId ? `wikidata-item-ai-verdict-${localId}` : undefined}
        onClick={(e) => {
          e.stopPropagation();
          setPopover({x: e.clientX, y: e.clientY});
        }}
      >
        <AiVerdictPill verdict={verdict} />
      </button>
      {popover && (
        <ClickDetailPopover
          x={popover.x}
          y={popover.y}
          title={verdictTitle(v.overall, v.judge_failure)}
          testId={localId ? `wikidata-item-ai-verdict-detail-${localId}` : undefined}
          panelClassName="w-[min(420px,calc(100vw-1.5rem))] max-h-[min(420px,70vh)]"
          onClose={() => setPopover(null)}
        >
          <div>
            <span className="muted">Verdict</span>
            <div><AiVerdictPill verdict={verdict} size="md" /></div>
          </div>
          <div>
            <span className="muted">Explanation</span>
            <div className="leading-relaxed whitespace-pre-wrap break-words text-ink">
              {v.reasoning?.trim()
                ? v.reasoning
                : "No written explanation was stored for this verdict. Re-run Verify with AI to refresh it."}
            </div>
          </div>
          {fixes.length > 0 && (
            <div>
              <span className="muted">Suggested fixes ({fixes.length})</span>
              <ul className="list-disc pl-4 space-y-1 leading-snug">
                {fixes.slice(0, 8).map((fix, i) => (
                  <li key={i}>
                    {typeof fix.target === "string" ? <b>{fix.target}: </b> : null}
                    {typeof fix.value === "string"
                      ? fix.value
                      : typeof fix.proposed === "string"
                        ? fix.proposed
                        : JSON.stringify(fix)}
                  </li>
                ))}
              </ul>
            </div>
          )}
          {(v.model || when) && (
            <div className="text-[10px] muted border-t border-white/5 pt-2">
              {v.model && <>Model: {v.model}</>}
              {v.model && when && " · "}
              {when && <>Judged {when}</>}
            </div>
          )}
        </ClickDetailPopover>
      )}
    </>
  );
}
